import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  ErrorSnackBarData,
  NormalSnackBarData,
  SnackBarService,
} from '@qtek/libs/snack-bar-core';
import { ITemplateData, TemplateDataHistory } from '@qtek/shared/models';
import { isNil } from '@qtek/shared/utils';
import {
  combineLatest,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DocumentTemplateDataService } from './document-templates-data.service';
import { DocumentTemplatesActions } from './document-templates.actions';
import {
  selectLastPublishedTemplateData,
  selectTemplateDataHistory,
} from './document-templates.reducer';
import { DocumentTemplatesService } from './document-templates.service';

@Injectable()
export class DocumentTemplatesEffects {
  private lastPublishTime = 0;
  connectDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentTemplatesActions.connectDocumentTemplates),
      switchMap(({ payload }) => {
        return this.documentTemplatesService
          .subscribe({ ...payload })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(DocumentTemplatesActions.disconnectDocumentTemplates)
              )
            )
          );
      }),
      map(({ op, res, sts, id }: any) => {
        switch (op) {
          case 'query':
            return res || !isNil(sts?.meta)
              ? DocumentTemplatesActions.queryDocumentTemplatesSuccess({
                  documentTemplates: res || [],
                  meta: sts?.meta,
                })
              : DocumentTemplatesActions.queryDocumentTemplatesFailure({
                  error: sts,
                });
          case 'ins':
            return res || !isNil(sts?.meta)
              ? DocumentTemplatesActions.createDocumentTemplatesSuccess({
                  documentTemplate: res,
                })
              : DocumentTemplatesActions.queryDocumentTemplatesFailure({
                  error: sts,
                });
          case 'del':
            return res || !isNil(sts?.meta)
              ? DocumentTemplatesActions.deleteDocumentTemplatesSuccess({
                  documentTemplateId: res._id,
                })
              : DocumentTemplatesActions.deleteDocumentTemplatesFailure({
                  error: sts,
                });
          case 'upd': {
            return res || !isNil(sts?.meta)
              ? DocumentTemplatesActions.updateDocumentTemplatesSuccess({
                  payload: res,
                })
              : DocumentTemplatesActions.updateDocumentTemplatesFailure({
                  error: sts,
                });
          }

          default:
            return {
              type: '[Document Templates] Unknown operation',
            };
        }
      })
    )
  );

  connectTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentTemplatesActions.connectToTemplate),
      switchMap(({ payload }) => {
        return this.documentTemplatesService
          .subscribeTemplate({ ...payload })
          .pipe(
            takeUntil(
              this.actions$.pipe(
                ofType(DocumentTemplatesActions.disconnectFromTemplate)
              )
            )
          );
      }),
      map(({ op, res, sts, id }: any) => {
        switch (op) {
          case 'query':
            return res || !isNil(sts?.meta)
              ? DocumentTemplatesActions.queryDocumentTemplatesSuccess({
                  documentTemplates: res,
                  meta: sts?.meta,
                })
              : DocumentTemplatesActions.queryDocumentTemplatesFailure({
                  error: sts,
                });
          case 'ins':
            return res || !isNil(sts?.meta)
              ? DocumentTemplatesActions.createDocumentTemplatesSuccess({
                  documentTemplate: res,
                })
              : DocumentTemplatesActions.queryDocumentTemplatesFailure({
                  error: sts,
                });
          case 'del':
            return res || !isNil(sts?.meta)
              ? DocumentTemplatesActions.deleteDocumentTemplatesSuccess({
                  documentTemplateId: res._id,
                })
              : DocumentTemplatesActions.deleteDocumentTemplatesFailure({
                  error: sts,
                });
          case 'upd': {
            return res || !isNil(sts?.meta)
              ? DocumentTemplatesActions.updateDocumentTemplatesSuccess({
                  payload: res,
                })
              : DocumentTemplatesActions.updateDocumentTemplatesFailure({
                  error: sts,
                });
          }

          default:
            return {
              type: '[Document Templates] Unknown operation',
            };
        }
      })
    )
  );

  connectTemplatesData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentTemplatesActions.connectTemplateData),
      switchMap(({ payload: { tmpId, ...rest } }) => {
        return this.documentTemplateDataService.subscribe(rest).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(DocumentTemplatesActions.disconnectTemplateData)
            )
          ),
          map(response => ({ ...response, tmpId }))
        );
      }),
      withLatestFrom(
        combineLatest([
          this.store.select(selectTemplateDataHistory),
          this.store.select(selectLastPublishedTemplateData),
        ])
      ),
      map(
        ([payload, [history, lastPublished]]: [
          any,
          [TemplateDataHistory, ITemplateData | null],
        ]) => {
          if (!history) {
            return { payload, isLastPublished: false };
          }
          const published = history.filter(({ _vsg }) => _vsg === 'published');
          const draft = history.find(({ _vsg }) => _vsg === 'draft');
          const lastPublishedInHistory = published[published.length - 1];
          const isLastPublishedInResponse =
            payload.res?._vts &&
            payload.res?._vts === lastPublishedInHistory?._vts;
          const notSet = !lastPublished && isLastPublishedInResponse;
          const newDraft =
            draft?._sVts !== lastPublished?._vts && isLastPublishedInResponse;

          return { payload, isLastPublished: notSet || newDraft };
        }
      ),
      map(({ payload: { op, res, sts, id } }: any) => {
        switch (op) {
          case 'query':
            return res
              ? DocumentTemplatesActions.getTemplateDataHistorySuccess({
                  templateDataHistory: res,
                })
              : DocumentTemplatesActions.getTemplateDataHistoryFailure({
                  error: sts,
                });
          case 'get':
            return res
              ? DocumentTemplatesActions.getTemplateDataSuccess({
                  templateData: res,
                })
              : DocumentTemplatesActions.getTemplateDataFailure({
                  error: sts,
                });

          default:
            return {
              type: '[Document Templates] Unknown operation',
            };
        }
      })
    )
  );

  createDocumentTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.createDocumentTemplates),
        tap(({ documentTemplateName, documentTemplateType }) => {
          this.documentTemplatesService.createDocumentTemplate(
            documentTemplateName,
            documentTemplateType
          );
        })
      ),
    { dispatch: false }
  );

  deleteDocumentTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.deleteDocumentTemplates),
        tap(({ documentTemplateId }) => {
          this.documentTemplatesService.deleteDocumentTemplate(
            documentTemplateId
          );
        })
      ),
    { dispatch: false }
  );

  updateDocumentTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.updateDocumentTemplates),
        tap(({ documentTemplateName, documentTemplateId }) => {
          this.documentTemplatesService.updateDocumentTemplate(
            documentTemplateName,
            documentTemplateId
          );
        })
      ),
    { dispatch: false }
  );

  updateDocumentTemplateData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.updateDocumentTemplatesData),
        tap(({ id, res }) => {
          this.documentTemplateDataService.updateDocumentTemplateData(id, res);
        })
      ),
    { dispatch: false }
  );

  createDocumentTemplateData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.createDocumentTemplateData),
        tap(({ tplId }) => {
          this.documentTemplateDataService.createDocumentTemplateData(tplId);
        })
      ),
    { dispatch: false }
  );

  getDocumentTemplateData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.getTemplateData),
        tap(({ id, dealId }) => {
          this.documentTemplateDataService.getTemplateData(id, dealId);
        })
      ),
    { dispatch: false }
  );

  getDocumentTemplateDataHistory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.getTemplateDataHistory),
        tap(({ id }) => {
          this.documentTemplateDataService.getTemplateDataHistory(id);
        })
      ),
    { dispatch: false }
  );

  showCreateSuccessSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.createDocumentTemplatesSuccess),
        tap(() => {
          const snackBarData: NormalSnackBarData = {
            type: 'NormalMessage',
            message: 'GNR_TEMPLATE_CREATED',
          };
          this.snackBarService.showSnackBar(snackBarData);
        })
      ),
    { dispatch: false }
  );

  showUpdateSuccessSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.updateDocumentTemplatesSuccess),
        filter(() => {
          const timeSincePublish = Date.now() - this.lastPublishTime;
          return timeSincePublish > 2000;
        }),
        tap(() => {
          const snackBarData: NormalSnackBarData = {
            type: 'NormalMessage',
            message: 'GNR_TEMPLATE_UPDATED',
          };
          this.snackBarService.showSnackBar(snackBarData);
        })
      ),
    { dispatch: false }
  );

  showDeleteSuccessSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.deleteDocumentTemplatesSuccess),
        tap(() => {
          const snackBarData: NormalSnackBarData = {
            type: 'NormalMessage',
            message: 'GNR_TEMPLATE_DELETED',
          };
          this.snackBarService.showSnackBar(snackBarData);
        })
      ),
    { dispatch: false }
  );

  showCreateFailureSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.createDocumentTemplatesFailure),
        tap(() => {
          const snackBarData: ErrorSnackBarData = {
            type: 'ErrorMessage',
            message: 'GNR_TEMPLATE_CREATE_FAILED',
          };
          this.snackBarService.showSnackBar(snackBarData);
        })
      ),
    { dispatch: false }
  );

  showUpdateFailureSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.updateDocumentTemplatesFailure),
        tap(() => {
          const snackBarData: ErrorSnackBarData = {
            type: 'ErrorMessage',
            message: 'GNR_TEMPLATE_UPDATE_FAILED',
          };
          this.snackBarService.showSnackBar(snackBarData);
        })
      ),
    { dispatch: false }
  );

  showDeleteFailureSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.deleteDocumentTemplatesFailure),
        tap(() => {
          const snackBarData: ErrorSnackBarData = {
            type: 'ErrorMessage',
            message: 'GNR_TEMPLATE_DELETE_FAILED',
          };
          this.snackBarService.showSnackBar(snackBarData);
        })
      ),
    { dispatch: false }
  );

  showPublishSuccessSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentTemplatesActions.updateDocumentTemplatesData),
        filter(({ res }) => res._vsg === 'published'),
        tap(() => {
          this.lastPublishTime = Date.now();
          const snackBarData: NormalSnackBarData = {
            type: 'NormalMessage',
            message: 'GNR_TEMPLATE_VERSION_PUBLISHED',
          };
          this.snackBarService.showSnackBar(snackBarData);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private documentTemplatesService: DocumentTemplatesService,
    private documentTemplateDataService: DocumentTemplateDataService,
    private snackBarService: SnackBarService
  ) {}
}
